import { colors, useToastController } from '@my/ui'
import { GroupAnnouncement, GroupPost } from 'app/types'
import { GroupPostOption, GroupPostOptions } from './group-post-options'
import { useBookmarkGroupPost, useRemoveBookmarkedGroupPost } from './commands'
import { getSavedGroupPostQuery, useSavedGroupPostQuery } from '../../queries'
import { useAuth } from 'app/provider/auth'
import { useState } from 'react'
import { Bookmark, Edit, Flag, MegaphoneOff, X } from '@tamagui/lucide-icons'
import {
  MuteUsernameModal,
  GroupPostEditWizard,
  ReportGroupPostModal,
  DeleteGroupPostModal,
  GroupAnnouncementEditWizard,
} from '..'
export interface GroupPostOptionsContainerProps {
  post: GroupPost | GroupAnnouncement
  onAfterDelete?: () => void
}

export const GroupPostOptionsContainer = (props: GroupPostOptionsContainerProps) => {
  const { post } = props
  const { user } = useAuth()
  const toast = useToastController()
  const [open, setOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [reportPostModalOpen, setReportPostModalOpen] = useState(false)
  const [muteUsernameModalOpen, setMuteUsernameModalOpen] = useState(false)

  const isCreator = post.creator.id === user?.id
  const isGroupOwner = post.group.isOwner
  const isGroupModerator = post.group.isModerator

  const { data: bookmarkedPost, loading: bookmarkedLoading } = useSavedGroupPostQuery({
    variables: {
      groupPostId: post.id,
    },
    skip: !user?.id || !open || isCreator,
  })

  const isAnnouncement = post.type === 'groupAnnouncement'
  const isBookmarked = !!bookmarkedPost?.SavedGroupPosts?.docs?.[0]?.id

  const [bookmarkGroupPost, { loading: bookmarkLoading }] = useBookmarkGroupPost()
  const [removeBookmarkedGroupPost, { loading: removeBookmarkLoading }] =
    useRemoveBookmarkedGroupPost()

  const handleRemoveBookmarkGroupPost = async () => {
    try {
      await removeBookmarkedGroupPost({
        variables: {
          groupPostId: post.id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getSavedGroupPostQuery,
            variables: {
              groupPostId: post.id,
            },
          },
        ],
      })
      toast.show('Bookmark removed')
    } catch (error) {
      console.error(error)
      toast.show('Something went wrong')
    }
  }

  const handleBookmarkGroupPost = async () => {
    try {
      await bookmarkGroupPost({
        variables: {
          groupPostId: post.id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getSavedGroupPostQuery,
            variables: {
              groupPostId: post.id,
            },
          },
        ],
      })
      toast.show('Bookmark added')
    } catch (error) {
      console.error(error)
      toast.show('Something went wrong')
    }
  }

  const options = [
    {
      ...(isCreator && {
        label: 'Edit Post',
        description: 'Edit this post',
        icon: <Edit size={24} />,
        onPress: () => {
          setOpen(false)
          setEditModalOpen(true)
        },
      }),
    },
    // {
    //   label: 'Turn on Notifications',
    //   description: 'Get notified about new comments',
    //   onPress: () => {
    //     setOpen(false)
    //     alert('Turn on notifications')
    //   },
    // },
    {
      ...(!isCreator && {
        label: 'Report This Post',
        description: 'Flag for review',
        icon: <Flag size={24} />,
        onPress: () => {
          setOpen(false)
          setReportPostModalOpen(true)
        },
      }),
    },
    {
      ...(!isCreator && {
        label: 'Mute username',
        description: 'Hide all posts from this person',
        icon: <MegaphoneOff size={24} />,
        onPress: () => {
          setOpen(false)
          setMuteUsernameModalOpen(true)
        },
      }),
    },
    {
      ...(!isCreator &&
        !isBookmarked && {
          label: 'Bookmark',
          description: 'Save post for later',
          icon: <Bookmark size={24} />,
          loading: bookmarkLoading || bookmarkedLoading,
          onPress: () => {
            setOpen(false)
            handleBookmarkGroupPost()
          },
        }),
    },
    {
      ...(!isCreator &&
        isBookmarked && {
          label: 'Remove Bookmark',
          description: 'Remove this post from your bookmarks',
          icon: <Bookmark fill={colors.black} size={24} />,
          loading: removeBookmarkLoading || bookmarkedLoading,
          onPress: () => {
            setOpen(false)
            handleRemoveBookmarkGroupPost()
          },
        }),
    },
    {
      ...((isCreator || isGroupOwner || isGroupModerator) && {
        label: 'Delete Post',
        description: 'Delete this post',
        icon: <X size={24} color="red" />,
        color: 'red',
        onPress: () => {
          setOpen(false)
          setDeleteModalOpen(true)
        },
      }),
    },
  ].filter((option) => option.label) as GroupPostOption[]

  return (
    <>
      <GroupPostOptions open={open} setOpen={setOpen} options={options} />
      {!isCreator && (
        <ReportGroupPostModal
          groupPostId={post.id}
          open={reportPostModalOpen}
          onOpenChange={setReportPostModalOpen}
        />
      )}
      {!isCreator && (
        <MuteUsernameModal
          groupPostId={post.id}
          mutee={post.creator}
          open={muteUsernameModalOpen}
          onOpenChange={setMuteUsernameModalOpen}
        />
      )}
      {(isCreator || isGroupOwner || isGroupModerator) && (
        <DeleteGroupPostModal
          type="post"
          groupPostId={post.id}
          groupId={post.group.id}
          userId={post.creator.id}
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          onAfterDelete={props.onAfterDelete}
        />
      )}
      {isCreator && !isAnnouncement && (
        <GroupPostEditWizard
          open={editModalOpen}
          user={user}
          onOpenChange={setEditModalOpen}
          post={post}
        />
      )}
      {isCreator && isAnnouncement && (
        <GroupAnnouncementEditWizard
          open={editModalOpen}
          user={user}
          onOpenChange={setEditModalOpen}
          announcement={post}
        />
      )}
    </>
  )
}
